const CONTENT_ROW_HEIGHT_ADJUSTMENT_PX = 15;

interface EqualizeSpreadsheetRowHeightOptions {
  forceBothRows?: boolean;
  includeXlsxReferenceHeight?: boolean;
}

const getElementZoomScale = (element: HTMLElement): number => {
  let zoomScale = 1;
  let currentElement: HTMLElement | null = element;

  while (currentElement) {
    const currentZoom = Number(window.getComputedStyle(currentElement).getPropertyValue('zoom'));
    if (Number.isFinite(currentZoom) && currentZoom > 0) {
      zoomScale *= currentZoom;
    }
    currentElement = currentElement.parentElement;
  }

  return zoomScale;
};

const getLogicalRowIndexFromTr = (row: HTMLTableRowElement): number | undefined => {
  const cell = row.querySelector<HTMLElement>('td[data-row-index]');
  if (!cell?.dataset.rowIndex) {
    return undefined;
  }

  const index = Number(cell.dataset.rowIndex);
  return Number.isNaN(index) ? undefined : index;
};

const getTitleRowSpan = (row: HTMLTableRowElement): number => {
  const cell = row.querySelector<HTMLTableCellElement>('td[data-row-index]');
  return cell?.rowSpan ?? 1;
};

const buildRowIndexMap = (rows: HTMLTableRowElement[]): Map<number, HTMLTableRowElement> => {
  const map = new Map<number, HTMLTableRowElement>();
  rows.forEach(row => {
    const index = getLogicalRowIndexFromTr(row);
    if (index !== undefined) {
      map.set(index, row);
    }
  });
  return map;
};

const setCellHeight = (cell: HTMLElement, height: number): void => {
  cell.style.setProperty('box-sizing', 'border-box');
  cell.style.setProperty('height', `${height}px`, 'important');
  cell.style.setProperty('vertical-align', 'middle');
};

const setRowCellsHeight = (row: HTMLTableRowElement, height: number): void => {
  Array.from(row.cells).forEach(cell => {
    setCellHeight(cell as HTMLElement, height);
  });
};

const applyRenderedRowHeight = (row: HTMLTableRowElement, renderedHeight: number): void => {
  const zoomScale = getElementZoomScale(row);
  const cssHeight = renderedHeight / zoomScale;
  const rowElement = row as HTMLElement;
  rowElement.style.height = `${cssHeight}px`;

  Array.from(row.cells).forEach(cell => {
    const cellEl = cell as HTMLElement;
    cellEl.style.boxSizing = 'border-box';
    cellEl.style.height = `${cssHeight}px`;
    cellEl.style.minHeight = `${cssHeight}px`;
    cellEl.style.maxHeight = `${cssHeight}px`;
    cellEl.style.verticalAlign = 'middle';
  });
};

const distributeContentRowHeights = (contentRows: HTMLTableRowElement[], targetTotal: number): void => {
  if (contentRows.length === 0) {
    return;
  }

  if (contentRows.length === 1) {
    setRowCellsHeight(contentRows[0], targetTotal);
    return;
  }

  const leadingRows = contentRows.slice(0, -1);
  const lastRow = contentRows.at(-1);
  if (!lastRow) {
    return;
  }
  const leadingHeight = leadingRows.reduce((sum, row) => sum + row.offsetHeight, 0);
  setRowCellsHeight(lastRow, Math.max(targetTotal - leadingHeight, 0));
};

const getContentRowsForTitleIndex = (
  logicalRowIndex: number,
  rowSpan: number,
  contentRowMap: Map<number, HTMLTableRowElement>
): HTMLTableRowElement[] => {
  const contentRows: HTMLTableRowElement[] = [];
  for (let offset = 0; offset < rowSpan; offset += 1) {
    const contentRow = contentRowMap.get(logicalRowIndex + offset);
    if (contentRow) {
      contentRows.push(contentRow);
    }
  }
  return contentRows;
};

const getForcedContentRowRenderedHeight = (
  contentRow: HTMLTableRowElement,
  includeXlsxReferenceHeight?: boolean
): number | undefined => {
  if (!includeXlsxReferenceHeight) {
    return undefined;
  }

  const forcedCell = contentRow.querySelector<HTMLElement>('td.xlsx-row-reference-cell.cell-with-references');
  if (!forcedCell) {
    return undefined;
  }

  // Row-reference chip rows prefer a compact height, but never below the browser-rendered row height.
  const forcedCssHeight = Number.parseFloat(window.getComputedStyle(forcedCell).maxHeight);
  if (!Number.isFinite(forcedCssHeight) || forcedCssHeight <= 0) {
    return undefined;
  }

  return forcedCssHeight * getElementZoomScale(contentRow);
};

const getForcedContentRowsRenderedHeight = (
  contentRows: HTMLTableRowElement[],
  includeXlsxReferenceHeight?: boolean
): number | undefined => {
  if (!includeXlsxReferenceHeight) {
    return undefined;
  }

  let hasForcedHeight = false;
  const renderedHeight = contentRows.reduce((sum, contentRow) => {
    const forcedHeight = getForcedContentRowRenderedHeight(contentRow, includeXlsxReferenceHeight);
    if (forcedHeight) {
      hasForcedHeight = true;
      return sum + forcedHeight;
    }
    return sum + contentRow.getBoundingClientRect().height;
  }, 0);

  return hasForcedHeight ? renderedHeight : undefined;
};

const equalizeTitleContentRowBlock = (
  titleRow: HTMLTableRowElement,
  contentRows: HTMLTableRowElement[],
  contentHeightAdjustment = 0
): void => {
  if (contentRows.length === 0) {
    return;
  }

  const titleHeight = titleRow.offsetHeight;
  const contentHeight = contentRows.reduce((sum, row) => sum + row.offsetHeight, 0);
  if (Math.abs(titleHeight - contentHeight) <= 1) {
    return;
  }

  const target = Math.max(titleHeight, contentHeight);
  const titleCell = titleRow.cells[0] as HTMLElement | undefined;
  if (titleCell) {
    setCellHeight(titleCell, target);
  }

  if (contentHeight < target) {
    distributeContentRowHeights(contentRows, target);

    if (contentHeightAdjustment > 0) {
      const residual = contentRows.reduce((sum, row) => sum + row.offsetHeight, 0) - titleRow.offsetHeight;
      if (residual > 0) {
        const lastRow = contentRows[contentRows.length - 1];
        const lastCell = lastRow.cells[0] as HTMLElement;
        const parsedHeight = Number.parseFloat(lastCell.style.height);
        if (!Number.isNaN(parsedHeight)) {
          setCellHeight(lastCell, parsedHeight - residual);
        }
      }
    }
  }
};

const forceTitleContentRowBlockHeight = (
  titleRow: HTMLTableRowElement,
  contentRows: HTMLTableRowElement[],
  includeXlsxReferenceHeight?: boolean
): void => {
  if (contentRows.length === 0) {
    return;
  }

  const titleHeight = titleRow.getBoundingClientRect().height;
  const contentHeight = contentRows.reduce((sum, row) => sum + row.getBoundingClientRect().height, 0);
  const forcedHeight = getForcedContentRowsRenderedHeight(contentRows, includeXlsxReferenceHeight);
  const hasMatchingRenderedHeights = Math.abs(titleHeight - contentHeight) < 0.01;

  if (!forcedHeight && hasMatchingRenderedHeights) {
    return;
  }

  const target = Math.ceil(Math.max(titleHeight, contentHeight, forcedHeight ?? 0));

  applyRenderedRowHeight(titleRow, target);

  if (contentRows.length === 1) {
    applyRenderedRowHeight(contentRows[0], target);
    return;
  }

  distributeContentRowHeights(contentRows, target);
};

/**
 * When the title column wraps long text, its rows can grow taller than the
 * corresponding rows in the content column (separate <table> elements in a flex
 * container). Read heights from the live DOM and write them onto the content
 * cells so both columns align (export preview, html2canvas capture, etc.).
 */
export const syncSpreadsheetTitleContentRowHeights = (
  titlesContainer: HTMLElement,
  contentContainer: HTMLElement,
  additionalContainers: HTMLElement[] = []
): void => {
  const titleRowMap = buildRowIndexMap(
    Array.from(titlesContainer.querySelectorAll<HTMLTableRowElement>('table tbody tr'))
  );
  // The totals column is a third sibling <table>, so it drifts exactly like the content
  // columns do when a title row grows.
  const followerRowMaps = [contentContainer, ...additionalContainers].map(container =>
    buildRowIndexMap(Array.from(container.querySelectorAll<HTMLTableRowElement>('table tbody tr')))
  );

  titleRowMap.forEach((titleRow, logicalRowIndex) => {
    const rowSpan = getTitleRowSpan(titleRow);

    followerRowMaps.forEach(followerRowMap => {
      const contentRows = getContentRowsForTitleIndex(logicalRowIndex, rowSpan, followerRowMap);
      if (contentRows.length === 0) {
        return;
      }

      const titleHeight = titleRow.offsetHeight;
      const contentHeight = contentRows.reduce((sum, row) => sum + row.offsetHeight, 0);
      if (titleHeight <= contentHeight) {
        return;
      }

      equalizeTitleContentRowBlock(titleRow, contentRows, CONTENT_ROW_HEIGHT_ADJUSTMENT_PX);
    });
  });
};

/**
 * Bi-directional row-height equalization between the titles column and the
 * content columns. Pairs rows by logical config index (`data-row-index`) so
 * title/content tables stay aligned even when rowspan collapses title-side rows.
 * Unlike {@link syncSpreadsheetTitleContentRowHeights}, this also expands title-side
 * cells when the content side is taller, so it is safe to run continuously in the
 * regular UI (not just during PDF capture).
 */
export const equalizeSpreadsheetTitleContentRowHeights = (
  titlesContainer: HTMLElement,
  contentContainer: HTMLElement,
  options: EqualizeSpreadsheetRowHeightOptions = {}
): void => {
  const titleRowMap = buildRowIndexMap(
    Array.from(titlesContainer.querySelectorAll<HTMLTableRowElement>('table tbody tr'))
  );
  const contentRowMap = buildRowIndexMap(
    Array.from(contentContainer.querySelectorAll<HTMLTableRowElement>('table tbody tr'))
  );

  titleRowMap.forEach((titleRow, logicalRowIndex) => {
    const rowSpan = getTitleRowSpan(titleRow);
    const contentRows = getContentRowsForTitleIndex(logicalRowIndex, rowSpan, contentRowMap);
    if (options.forceBothRows) {
      forceTitleContentRowBlockHeight(titleRow, contentRows, options.includeXlsxReferenceHeight);
      return;
    }

    equalizeTitleContentRowBlock(titleRow, contentRows);
  });
};

/** Runs {@link syncSpreadsheetTitleContentRowHeights} for every titles/content pair under `root`. */
export const syncSpreadsheetTitleContentRowHeightsInSubtree = (root: HTMLElement): void => {
  root.querySelectorAll<HTMLElement>('.titles-columns-container').forEach(titlesContainer => {
    const parent = titlesContainer.parentElement;
    if (!parent) {
      return;
    }

    const contentContainer = parent.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
    if (!contentContainer) {
      return;
    }

    const totalsContainer = parent.querySelector<HTMLElement>('[id="spreadsheet-table__total-column"]');

    syncSpreadsheetTitleContentRowHeights(titlesContainer, contentContainer, totalsContainer ? [totalsContainer] : []);
  });
};
